/**
 * QUIL — MSAL AUTH CONFIG
 * Requirements §11.3 | Build Doc §10
 */

import { PublicClientApplication, LogLevel } from '@azure/msal-browser'
import { APP_FOLDER_SCOPES, buildMsalConfig } from './authConfig'
import { configureAuthFlags } from './authFlags'
import { InteractiveSignInRequiredError } from './authToken'
import type { TokenGetter } from './authToken'
import { isRedirectResponse, recordFirstBoot } from './firstBoot'
import { bindScope } from './scopedToken'
import type { StoreScope } from './storageScope'
import { FIRST_BOOT_KEY } from './scopeKeys'
import { getActiveDb, NoStoreError } from './db'
import { setBoundAccountId } from './boundAccount'

// The shared parts of the MSAL config are canonical now (app-platform/shared/authConfig.ts) —
// authority, cache location, and the postLogoutRedirectUri that four of the five apps used to
// inherit from MSAL. Fix them in the hub, never here; shared.drift.test.ts enforces it.
// Design: app-platform/design/W9-auth.md.

// Azure App Registration — personal Microsoft account, consumers authority
const QUIL_CLIENT_ID = 'ae1a6c8c-63b9-4821-83b4-6e8a904a2325'

// Quil's flags. The keys are new: this app had none of the three before W9, so there is no
// existing spelling to preserve.
configureAuthFlags({
  returningVisitor: 'quil.hasSignedInBefore',
  deliberateSignOut: 'quil.signedOut',
  autoLoginAttempted: 'quil.autoLoginAttempted',
})

const msalConfig = buildMsalConfig({
  clientId: QUIL_CLIENT_ID,
  scopes: APP_FOLDER_SCOPES,
  // Quil's own: a PII-filtered dev logger. No sibling wants MSAL chatter in its console, so this
  // stays per-app rather than moving into the shared config.
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return
        if (import.meta.env.DEV) {
          console.log(`[MSAL ${LogLevel[level]}] ${message}`)
        }
      },
      logLevel: import.meta.env.DEV ? LogLevel.Info : LogLevel.Warning,
    },
  },
})

// SEC1: least-privilege Graph scope. `Files.ReadWrite.AppFolder` grants access ONLY to
// the app's own special folder (physically /Apps/Quil), never the rest of the drive, so
// a leaked token can't touch anything outside Quil's data. NB: this is a NEW scope — the
// first sign-in after this ships prompts Kamrin to re-consent; onedrive.ts addresses
// everything via `special/approot` because absolute /root:/Apps/Quil paths are denied
// under this scope.
export const GRAPH_SCOPES = [...APP_FOLDER_SCOPES]
export const GRAPH_BASE = 'https://graph.microsoft.com/v1.0'
// Logical root the app speaks in; onedrive.ts translates it to approot-relative Graph
// addressing. This is also approot's physical path, so delta parentReference.path
// parsing (sync.parseDeltaPath) is unchanged.
export const QUIL_ONEDRIVE_ROOT = '/Apps/Quil'

export const msalInstance = new PublicClientApplication(msalConfig)

// Initialize once at module load. AuthContext awaits this before reading accounts.
// handleRedirectPromise consumes the redirect response on return (null on a normal
// load) and we promote that account to the active account; failing that we promote the
// first cached account, so a returning device restores its session without re-prompting.
// NB (MSAL v4+): localStorage auth artifacts are ENCRYPTED with a key held in a session
// cookie that dies on browser close — so the account can't be restored on reopen unless
// the user picked "Keep me signed in" (KMSI), a prompt that never appears in Kamrin's
// Edge+passkey flow (and there's no config to force it). So AuthGate AUTO-starts the
// redirect on a signed-out cold start; the persistent Microsoft/passkey session makes it a
// silent bounce back in, no click. See build/decisions.md (Auth 2026-07-06).
export const msalReady: Promise<void> = msalInstance
  .initialize()
  .then(async () => {
    // W12 P2 evidence (design section 3.1, decision 2): which account was ALREADY signed in at the
    // first boot of this build. Recorded here, after initialize() has loaded MSAL's cache and BEFORE
    // the redirect below is handled (which would turn a sign-in's return leg into an account),
    // because nothing afterwards can tell the two apart. Only the first boot ever writes.
    recordFirstBoot(FIRST_BOOT_KEY, msalInstance.getAllAccounts(), { redirectInFlight: isRedirectResponse(window.location) })
    try {
      const result = await msalInstance.handleRedirectPromise()
      if (result?.account) msalInstance.setActiveAccount(result.account)
    } catch {
      // Swallow: can occur on corrupted cache state; not fatal.
    }
    if (!msalInstance.getActiveAccount()) {
      const [first] = msalInstance.getAllAccounts()
      if (first) msalInstance.setActiveAccount(first)
    }
  })

// ─── The token, bound to a store (W12 A5) ─────────────────────────────────────

/**
 * The getter every Graph call goes through once a store is bound: built from the store's scope, so a
 * tab working on A's store can never flush with B's token. Null before a store is bound (signed out,
 * or deciding which store to open).
 */
let boundToken: TokenGetter | null = null

/**
 * Bind Graph's token to `scope` (the store the signed-in account opened, or, for P3's listing before
 * the store is decided, the account's prospective scope). Naming an account other than the scope's
 * throws `ScopeMismatchError` and binds nothing. If the ACTIVE account later stops being the scope's,
 * `onAccountChanged` is told once, so the tab can stop and reload.
 */
export function bindTokenScope(
  scope: StoreScope,
  opts: { accountId: string; onAccountChanged?: (activeAccountId: string | null) => void },
): void {
  setBoundAccountId(opts.accountId)
  boundToken = bindScope(msalInstance, scope, {
    accountId: opts.accountId,
    scopes: GRAPH_SCOPES,
    ...(opts.onAccountChanged ? { onAccountChanged: opts.onAccountChanged } : {}),
  })
}

/** Forget the binding (tests, and a store with no sign-in behind it). */
export function unbindTokenScope(): void {
  boundToken = null
  setBoundAccountId(null)
}

/**
 * A Graph access token for the BOUND store's account, refreshed silently where possible. There is
 * deliberately no fallback to the active account: with no binding a request is refused, as "sign in"
 * when a store is open without an account (the offline path) and as a missing store otherwise.
 */
export async function acquireBoundToken(options?: { forceRefresh?: boolean }): Promise<string> {
  if (boundToken) return boundToken(options)
  throw getActiveDb() ? new InteractiveSignInRequiredError() : new NoStoreError()
}
